import React, { useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  BookOpen,
  TrendingUp,
  Award,
  Video,
  ListChecks,
  User,
  Settings,
  HelpCircle,
  LogOut,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import ComingSoonWrapper from "../../../components/ComingSoonWrapper";
import {
  microLabel,
  accentBarCard,
  iconSize,
  motionStaggerChild,
  motionHoverLift,
} from "../../../lib/design-system";
import { cn } from "../../../lib/utils";

// ---------------------------------------------------------------------------
// NAVIGATION CONSTANTS
// ---------------------------------------------------------------------------

const MAIN_MENU_ITEMS = [
  {
    id: "Dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    comingSoon: false,
  },
  {
    id: "Courseware",
    label: "Courses",
    icon: BookOpen,
    comingSoon: false,
  },
  {
    id: "MyStats",
    label: "Performance",
    icon: TrendingUp,
    comingSoon: true,
    estimatedRelease: "Q2 2026",
  },
  {
    id: "Gradebook",
    label: "Grades",
    icon: Award,
    comingSoon: false,
  },
  {
    id: "LiveClass",
    label: "Live Sessions",
    icon: Video,
    comingSoon: true,
    estimatedRelease: "Q3 2026",
  },
  // {
  //   id: "ToDo",
  //   label: "Tasks",
  //   icon: ListChecks,
  //   comingSoon: false,
  // },
];

const ACCOUNT_ITEMS = [
  {
    id: "Profile",
    label: "Profile",
    icon: User,
    comingSoon: false,
  },
  {
    id: "Settings",
    label: "Settings",
    icon: Settings,
    comingSoon: false,
  },
  {
    id: "Help",
    label: "Help Center",
    icon: HelpCircle,
    comingSoon: true,
    estimatedRelease: "Q2 2026",
  },
];

// ---------------------------------------------------------------------------
// HELPER COMPONENTS
// ---------------------------------------------------------------------------

/** Section label for "MAIN MENU" and "ACCOUNT" */
const SectionLabel = React.memo(({ children, isCollapsed, isMobileOpen }) => (
  <AnimatePresence mode="wait">
    {(!isCollapsed || isMobileOpen) && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="px-4 mb-3 mt-8 text-xs uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500"
      >
        {children}
      </motion.div>
    )}
  </AnimatePresence>
));
SectionLabel.displayName = "SectionLabel";

/** Individual navigation button with elegant selected state */
const NavItem = React.memo(
  ({ item, isActive, isCollapsed, isMobileOpen, onClick, index }) => {
    const Icon = item.icon;
    return (
      <motion.button
        {...motionStaggerChild(index, 0)}
        onClick={onClick}
        whileHover={motionHoverLift}
        whileTap={{ scale: 0.98 }}
        className={cn(
          "relative w-full flex items-center gap-4 px-4 py-3 rounded-sm transition-all duration-200",
          isActive
            ? "bg-accent1 text-white font-semibold shadow-sm"
            : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-medium"
        )}
      >
        {/* Icon */}
        <Icon className="h-5 w-5 flex-shrink-0" />

        {/* Label */}
        <AnimatePresence>
          {(!isCollapsed || isMobileOpen) && (
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: "auto" }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.2 }}
              className="whitespace-nowrap text-[15px]"
            >
              {item.label}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    );
  }
);
NavItem.displayName = "NavItem";


// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------

const StudentDashboardSidebar = ({
  activeSection,
  setActiveSection,
  isMobileOpen,
  setIsMobileOpen,
}) => {
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = useCallback(() => {
    logout();
    navigate("/login");
  }, [logout, navigate]);

  const handleItemClick = useCallback(
    (item) => {
      if (!item.comingSoon) {
        setActiveSection(item.id);
        // Close mobile menu when item is clicked
        if (setIsMobileOpen) {
          setIsMobileOpen(false);
        }
      }
    },
    [setActiveSection, setIsMobileOpen]
  );

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed lg:static inset-y-0 left-0 z-50 ${
          isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        } ${
          isCollapsed && !isMobileOpen ? "lg:w-20" : "w-64"
        } dark:bg-gray-900 bg-white shadow-md transition-all duration-300 border-r border-gray-200 dark:border-gray-700 flex flex-col justify-between h-screen`}
        onMouseEnter={() => !isMobileOpen && setIsCollapsed(false)}
        onMouseLeave={() => !isMobileOpen && setIsCollapsed(true)}
      >
        {/* Mobile Close Button */}
        {isMobileOpen && (
          <button
            onClick={() => setIsMobileOpen(false)}
            className="absolute top-4 right-4 p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-800 lg:hidden"
          >
            <X className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </button>
        )}

        {/* Top Section */}
        <div>
          {/* Header Logo */}
          <div className="mt-8 mb-2 flex items-center justify-center px-6 gap-2">
            {isCollapsed && !isMobileOpen ? (
              <img
                src="/Logo_new_transparent.png"
                alt="Acaera"
                className="transition-all duration-300 object-contain w-[50px] h-[50px] dark:invert dark:hue-rotate-180"
              />
            ) : (
              <div className="flex items-center gap-2">
                <img
                  src="/Logo_new_transparent.png"
                  alt="Acaera Logo"
                  className="transition-all duration-300 object-contain w-[45px] h-[45px] dark:invert dark:hue-rotate-180"
                />
                <span className="font-bold text-[#1a365d] dark:text-white text-3xl tracking-wide">
                  Acaera
                </span>
              </div>
            )}
          </div>

          {/* Main Menu Section */}
          <SectionLabel isCollapsed={isCollapsed} isMobileOpen={isMobileOpen}>
            MAIN MENU
          </SectionLabel>

          {/* Main Navigation */}
          <ul className="space-y-1 px-2">
            {MAIN_MENU_ITEMS.map((item, index) => (
              <li key={item.id}>
                <ComingSoonWrapper
                  isComingSoon={item.comingSoon}
                  featureName={item.label}
                  estimatedRelease={item.estimatedRelease}
                  onClick={() => handleItemClick(item)}
                >
                  <NavItem
                    item={item}
                    isActive={activeSection === item.id || (item.id === "Courseware" && activeSection === "CourseDetail")}
                    isCollapsed={isCollapsed}
                    isMobileOpen={isMobileOpen}
                    onClick={() => handleItemClick(item)}
                    index={index}
                  />
                </ComingSoonWrapper>
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom Section - Account */}
        <div className="mb-6 px-2">
          <SectionLabel isCollapsed={isCollapsed} isMobileOpen={isMobileOpen}>
            ACCOUNT
          </SectionLabel>

          <div className="space-y-1">
            {ACCOUNT_ITEMS.map((item, index) => (
              <li key={item.id} className="list-none">
                <ComingSoonWrapper
                  isComingSoon={item.comingSoon}
                  featureName={item.label}
                  estimatedRelease={item.estimatedRelease}
                  onClick={() => handleItemClick(item)}
                >
                  <NavItem
                    item={item}
                    isActive={activeSection === item.id}
                    isCollapsed={isCollapsed}
                    isMobileOpen={isMobileOpen}
                    onClick={() => handleItemClick(item)}
                    index={MAIN_MENU_ITEMS.length + index}
                  />
                </ComingSoonWrapper>
              </li>
            ))}

            {/* Logout Button */}
            <motion.button
              {...motionStaggerChild(6 + ACCOUNT_ITEMS.length, 0)}
              onClick={handleLogout}
              whileHover={motionHoverLift}
              whileTap={{ scale: 0.98 }}
              className={cn(
                "relative w-full flex items-center gap-4 px-4 py-3 rounded-sm transition-all duration-200",
                "text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 font-medium"
              )}
            >
              {/* Icon */}
              <LogOut className="h-5 w-5 flex-shrink-0" />

              {/* Label */}
              <AnimatePresence>
                {(!isCollapsed || isMobileOpen) && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: "auto" }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.2 }}
                    className="whitespace-nowrap text-[15px]"
                  >
                    Logout
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentDashboardSidebar;
